import type { IconType } from 'react-icons';
import { FaHospitalUser, FaUserDoctor, FaUserNurse, FaUserGear } from 'react-icons/fa6';
import { IoCalendarOutline, IoDocumentTextOutline, IoPricetagsOutline } from 'react-icons/io5';
import {
  TbBox,
  TbClock24,
  TbStethoscope,
  TbPackages,
  TbCategory,
  TbUsersGroup,
  TbCash,
  TbFolders,
} from 'react-icons/tb';
import { MdOutlineStickyNote2 } from 'react-icons/md';
import dayjs from '@app/utils/dayjs-setup';
import type { IEnv } from '@app/enviroment/env.type';
import type { TranslationKeys } from '@app/i18n/translationKeys';
import {
  APP_PREFIX,
  CAREGIVER_GROUP_PAGE,
  CAREGIVERS_PREFIX,
  CLIENT_CATEGORY_PAGE,
  CLIENTS_PREFIX,
  DOCTOR_PREFIX,
  EDIT_CAREGIVER,
  EDIT_CLIENT,
  EDIT_DOCTOR,
  EDIT_SERVICE,
  EDIT_SERVICE_PACKAGE,
  EDIT_SERVICE_REQUEST_ARRANGEMENT,
  EDIT_USER,
  EDIT_VISIT,
  INVENTORY_CATEGORY_PAGE,
  INVENTORY_PAGE,
  PAYROLL_PAGE,
  SERVICE_PACKAGE_PREFIX,
  SERVICE_PREFIX,
  SERVICE_REQUESTS_MANAGEMENT_PAGE,
  SERVICE_TYPE_PAGE,
  SHIFTS_PAGE,
  USERS_PREFIX,
  VIEW_CLIENT,
  VISITS_PAGE,
} from '@app/routes/urls';
import type { SearchItem } from './types';

export interface EntityContext {
  isOffice: boolean;
  isAdmin: boolean;
  flag: (key: keyof IEnv) => boolean;
}

export interface EntityDef {
  type: string;
  labelKey: TranslationKeys;
  icon: IconType;
  route: (item: SearchItem) => string;
  enabled: (ctx: EntityContext) => boolean;
  /** List page that reads ?search=; undefined hides "See all in list". */
  listPath?: string;
}

const path = (...segments: string[]) => `/${[APP_PREFIX, ...segments].join('/')}`;
const withId = (template: string, id: number | unknown) => template.replace(':id', String(id));
const office = (ctx: EntityContext) => ctx.isOffice;

// Fixed palette order: the groups render in this order whatever the API returns.
export const ENTITY_REGISTRY: EntityDef[] = [
  {
    type: 'client',
    labelKey: 'global-search-group-client',
    icon: FaHospitalUser,
    enabled: office,
    route: (i) => path(CLIENTS_PREFIX, withId(EDIT_CLIENT, i.id)),
    listPath: path(CLIENTS_PREFIX, 'list'),
  },
  {
    type: 'visit',
    labelKey: 'global-search-group-visit',
    icon: IoCalendarOutline,
    enabled: office,
    route: (i) => path(VISITS_PAGE, withId(EDIT_VISIT, i.id)),
    listPath: path(VISITS_PAGE, 'list'),
  },
  {
    type: 'service_request',
    labelKey: 'global-search-group-service-request',
    icon: IoDocumentTextOutline,
    enabled: office,
    route: (i) => path(SERVICE_REQUESTS_MANAGEMENT_PAGE, withId(EDIT_SERVICE_REQUEST_ARRANGEMENT, i.id)),
    listPath: path(SERVICE_REQUESTS_MANAGEMENT_PAGE, 'list'),
  },
  {
    type: 'caregiver',
    labelKey: 'global-search-group-caregiver',
    icon: FaUserNurse,
    enabled: office,
    route: (i) => path(CAREGIVERS_PREFIX, withId(EDIT_CAREGIVER, i.id)),
    listPath: path(CAREGIVERS_PREFIX, 'list'),
  },
  {
    type: 'note',
    labelKey: 'global-search-group-note',
    icon: MdOutlineStickyNote2,
    enabled: office,
    route: (i) =>
      i.meta.visitId
        ? `${path(VISITS_PAGE, withId(EDIT_VISIT, i.meta.visitId))}?tab=notes`
        : path(CLIENTS_PREFIX, withId(VIEW_CLIENT, i.meta.clientId)),
  },
  {
    type: 'doctor',
    labelKey: 'global-search-group-doctor',
    icon: FaUserDoctor,
    enabled: office,
    route: (i) => path(DOCTOR_PREFIX, withId(EDIT_DOCTOR, i.id)),
    listPath: path(DOCTOR_PREFIX, 'list'),
  },
  {
    type: 'shift',
    labelKey: 'global-search-group-shift',
    icon: TbClock24,
    enabled: office,
    route: (i) => `${path(SHIFTS_PAGE)}?caregiver=${i.meta.caregiverId}&date=${dayjs(i.sortAt).format('YYYY-MM-DD')}`,
  },
  {
    type: 'user',
    labelKey: 'global-search-group-user',
    icon: FaUserGear,
    enabled: office,
    route: (i) => path(USERS_PREFIX, withId(EDIT_USER, i.id)),
    listPath: path(USERS_PREFIX, 'list'),
  },
  {
    type: 'service',
    labelKey: 'global-search-group-service',
    icon: TbStethoscope,
    enabled: office,
    route: (i) => path(SERVICE_PREFIX, withId(EDIT_SERVICE, i.id)),
    listPath: path(SERVICE_PREFIX, 'list'),
  },
  {
    type: 'service_package',
    labelKey: 'global-search-group-service-package',
    icon: TbPackages,
    enabled: office,
    route: (i) => path(SERVICE_PACKAGE_PREFIX, withId(EDIT_SERVICE_PACKAGE, i.id)),
    listPath: path(SERVICE_PACKAGE_PREFIX, 'list'),
  },
  {
    type: 'service_type',
    labelKey: 'global-search-group-service-type',
    icon: IoPricetagsOutline,
    enabled: office,
    route: (i) => `${path(SERVICE_TYPE_PAGE)}?edit=${i.id}`,
    listPath: path(SERVICE_TYPE_PAGE),
  },
  {
    type: 'client_category',
    labelKey: 'global-search-group-client-category',
    icon: TbCategory,
    enabled: office,
    route: (i) => `${path(CLIENT_CATEGORY_PAGE)}?edit=${i.id}`,
    listPath: path(CLIENT_CATEGORY_PAGE),
  },
  {
    type: 'caregiver_group',
    labelKey: 'global-search-group-caregiver-group',
    icon: TbUsersGroup,
    enabled: office,
    route: (i) => `${path(CAREGIVER_GROUP_PAGE)}?edit=${i.id}`,
    listPath: path(CAREGIVER_GROUP_PAGE),
  },
  {
    type: 'inventory_item',
    labelKey: 'global-search-group-inventory-item',
    icon: TbBox,
    enabled: (c) => c.isOffice && c.flag('INVENTORY_ENABLED'),
    route: (i) => `${path(INVENTORY_PAGE)}?item=${i.id}`,
  },
  {
    type: 'inventory_category',
    labelKey: 'global-search-group-inventory-category',
    icon: TbFolders,
    enabled: (c) => c.isOffice && c.flag('INVENTORY_ENABLED'),
    route: (i) => `${path(INVENTORY_CATEGORY_PAGE)}?edit=${i.id}`,
    listPath: path(INVENTORY_CATEGORY_PAGE),
  },
  {
    type: 'payroll_run',
    labelKey: 'global-search-group-payroll-run',
    icon: TbCash,
    enabled: (c) => c.isAdmin && c.flag('PAYROLL_ENABLED'),
    route: (i) => path(PAYROLL_PAGE, String(i.id)),
  },
];

const BY_TYPE = new Map(ENTITY_REGISTRY.map((d) => [d.type, d]));

export const entityDef = (type: string): EntityDef | undefined => BY_TYPE.get(type);
export const enabledEntities = (ctx: EntityContext): EntityDef[] => ENTITY_REGISTRY.filter((d) => d.enabled(ctx));
